// src/components/importData/index.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { SOURCE_OPTIONS } from './utils/constants';
import { handleFile } from './utils/parsers/index.js';
 
import FileUploader from './components/FileUploader';
import DataTable from './components/DataTable';
import { toast } from 'react-hot-toast';
import { collection, doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../services/firebase/config';

const ImportData = ({ activePortfolio, refreshPortfolios }) => {
  const navigate = useNavigate();
  const [selectedSource, setSelectedSource] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [showUploader, setShowUploader] = useState(true);

  const handleSourceChange = (source) => {
    setSelectedSource(source);
    setError(null);
    setStockData([]);
    setShowUploader(true);
  };

  const handleFileUpload = async (file) => {
    try {
      setIsLoading(true);
      setError(null);

      const source = SOURCE_OPTIONS.find(opt => opt.id === selectedSource);
      if (!source) {
        throw new Error('Lütfen önce bir veri kaynağı seçin');
      }

      const result = await handleFile(file, selectedSource);
      setStockData(result);
      setShowUploader(false);

    } catch (err) {
      console.error('File processing error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValueChange = (index, field, value) => {
    const updatedStocks = [...stockData];
    
    if (['shares', 'price', 'total', 'cost'].includes(field)) {
      value = parseFloat(value) || 0;
      
      updatedStocks[index][field] = value;
      
      if (field === 'shares' || field === 'price') {
        updatedStocks[index].total = updatedStocks[index].shares * updatedStocks[index].price;
      }
      
      if (field === 'cost' || field === 'total') {
        updatedStocks[index].profitLoss = updatedStocks[index].total - (updatedStocks[index].cost * updatedStocks[index].shares);
      }
    } else {
      updatedStocks[index][field] = value;
    }
    
    setStockData(updatedStocks);
  };

  const handleDeleteRow = (index) => {
    setStockData(prevData => prevData.filter((_, i) => i !== index));
  };

  const resetUploader = () => {
    setShowUploader(true);
    setStockData([]);
    setError(null);
  };

  const handleImportData = async () => {
    if (!activePortfolio || !auth.currentUser?.email) {
      toast.error('Aktif portföy bulunamadı');
      return;
    }
 
 

    // src/components/importData/index.js (devamı)
    setIsImporting(true);
    try {
      const batch = writeBatch(db);
      
      const positionsRef = collection(db, 
        'users', 
        auth.currentUser.email, 
        'portfolios', 
        activePortfolio.id, 
        'positions'
      );

      for (const stock of stockData) {
        const positionRef = doc(positionsRef, stock.symbol);
        batch.set(positionRef, {
          symbol: stock.symbol,
          quantity: stock.shares,
          avgCost: stock.cost,
          lastPrice: stock.price,
          value: stock.total,
          profitLoss: stock.profitLoss,
          currency: "TRY",
          exchange: "BIST",
          secType: "STK",
          lastUpdated: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      await batch.commit();
      await refreshPortfolios();
      toast.success('Veriler başarıyla aktarıldı');
      
      // Pozisyonlar sayfasına yönlendir
      navigate('/portfolio/positions');
      
    } catch (error) {
      console.error('Veri aktarım hatası:', error);
      toast.error('Veriler aktarılırken bir hata oluştu');
    } finally {
      setIsImporting(false);
    }
  };

  const totalPortfolioValue = stockData.reduce((sum, stock) => sum + stock.total, 0);

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Portfolio Veri Kaynağı</label>
        <select
          value={selectedSource}
          onChange={(e) => handleSourceChange(e.target.value)}
          className="block w-full rounded-lg border border-gray-700 bg-gray-800/90 px-4 py-2.5 text-gray-200"
        >
          <option value="">Veri kaynağı seçin</option>
          {SOURCE_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-400/10 text-red-400 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {showUploader && selectedSource && (
        <FileUploader
          source={SOURCE_OPTIONS.find(opt => opt.id === selectedSource)}
          onFileUpload={handleFileUpload}
          isLoading={isLoading}
        />
      )}

      {isLoading ? (
        <div className="text-center p-4">Yükleniyor...</div>
      ) : stockData.length > 0 ? (
        <>
          <DataTable
            data={stockData}
            onValueChange={handleValueChange}
            onDeleteRow={handleDeleteRow}
            onReset={resetUploader}
            totalValue={totalPortfolioValue}
          />
          <div className="flex justify-end mt-4">
            <button
              onClick={handleImportData}
              disabled={isImporting}
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-600"
            >
              {isImporting ? 'Aktarılıyor...' : 'Verileri İçe Aktar'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ImportData;